import { supabase } from "@/integrations/supabase/client";

/**
 * Notification categories used across Xamseen. Security and account
 * notifications are always delivered and are intentionally not listed here.
 */
export const NOTIFICATION_CATEGORIES = [
  { key: "community", label: "Community activity", hint: "Follows, invites and new posts in your Spaces." },
  { key: "replies", label: "Replies and interactions", hint: "Comments, replies, answers and reactions on your content." },
  { key: "reports", label: "Reports and issue updates", hint: "Status changes, verifications, official responses and resolutions." },
  { key: "projects", label: "Projects", hint: "Updates on community Projects you joined." },
  { key: "product", label: "Product updates", hint: "Occasional news about new Xamseen features." },
] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number]["key"];

export type NotificationPreferences = {
  inapp_community: boolean;
  inapp_replies: boolean;
  inapp_reports: boolean;
  inapp_projects: boolean;
  inapp_product: boolean;
  email_community: boolean;
  email_replies: boolean;
  email_reports: boolean;
  email_projects: boolean;
  email_product: boolean;
};

export const DEFAULT_PREFERENCES: NotificationPreferences = {
  inapp_community: true,
  inapp_replies: true,
  inapp_reports: true,
  inapp_projects: true,
  inapp_product: true,
  email_community: false,
  email_replies: true,
  email_reports: true,
  email_projects: true,
  email_product: false,
};

const COLUMNS =
  "inapp_community, inapp_replies, inapp_reports, inapp_projects, inapp_product, email_community, email_replies, email_reports, email_projects, email_product";

/** Reads the member's preferences, falling back to defaults when none exist. */
export async function getNotificationPreferences(userId: string): Promise<NotificationPreferences> {
  const { data } = await supabase
    .from("notification_preferences")
    .select(COLUMNS)
    .eq("user_id", userId)
    .maybeSingle();
  return { ...DEFAULT_PREFERENCES, ...(data ?? {}) } as NotificationPreferences;
}

/** Upserts the member's preferences. RLS keeps this scoped to the signed-in user. */
export async function saveNotificationPreferences(userId: string, prefs: NotificationPreferences) {
  const { error } = await supabase
    .from("notification_preferences")
    .upsert({ user_id: userId, ...prefs }, { onConflict: "user_id" });
  if (error) throw new Error(error.message);
}
